import { EventDetails } from 'types/Event'
import { Seat } from 'types/Location'

export interface SeatLabelData {
    row: number // 1-based
    seatNumber: number // 1-based
    side: 'BAL' | 'JOBB'
    label: string // "{row}. sor, {seatNumber}. szék (BAL/JOBB)"
}

/**
 * Generate seat label data for given seat grid.
 * Adds custom gallery name prefix for gallery seats.
 */
export const generateSeatLabelData = (
    row: number,
    column: number,
    totalColumns: number,
    seats: Omit<Seat, 'seat_id'>[],
    middleColumn?: number,
    isGallery: boolean = false,
    galleryName: string = 'Karzat',
): SeatLabelData | null => {
    const activeMiddleColumn = middleColumn ?? Math.ceil(totalColumns / 2)

    const seatExists = seats?.some((seat) => seat.row === row && seat.column === column)
    if (!seatExists) return null

    const rowNumber = row + 1
    const side = column < activeMiddleColumn ? 'BAL' : 'JOBB'

    let seatNumber: number
    if (side === 'BAL') {
        // Count seats with column less than or equal to current column on left side
        seatNumber = seats.filter((s) => s.row === row && s.column < activeMiddleColumn && s.column <= column).length
    } else {
        // For right side, count seats with column greater or equal to middleColumn and less or equal to current
        // We count seats from right to left for numbering on the right side
        const rightSideSeats = seats.filter((s) => s.row === row && s.column >= activeMiddleColumn).sort((a, b) => b.column - a.column) // descending order of column

        seatNumber = rightSideSeats.findIndex((s) => s.column === column) + 1
    }

    return {
        row: rowNumber,
        seatNumber,
        side,
        label: `${rowNumber}. sor, ${seatNumber}. szék (${isGallery ? `${galleryName} ` : ''}${side})`,
    }
}

export function getSeatLabelForSeatId(event: EventDetails, seatId: string): string | null {
    const normalSeats = event.location.auditorium.seats
    const gallerySeats = event.location.auditorium.gallery?.seats || []

    // Find seat in either normal or gallery
    const seat = normalSeats.find((s) => s.seat_id === seatId) || gallerySeats.find((s) => s.seat_id === seatId)
    if (!seat) {
        return null
    }

    // Check if gallery
    const isGallery = gallerySeats?.some((s) => s.seat_id === seatId)

    // Get info for generateSeatLabelData params
    const seatsArray = isGallery ? gallerySeats : normalSeats
    const totalColumns = isGallery ? event.location.auditorium.gallery?.columns ?? 0 : event.location.auditorium.columns
    const middleColumn = isGallery ? event.location.auditorium.gallery?.middle_column : event.location.auditorium.middle_column
    const galleryName = event.location.auditorium.gallery?.gallery_name || 'Karzat'

    // Generate label data and return label string or null
    const labelData = generateSeatLabelData(seat.row, seat.column, totalColumns, seatsArray, middleColumn, isGallery, galleryName)

    return labelData?.label ?? null
}

/**
 * Returns a deterministic label for general ticket selling.
 * Uses the seat's position in the selectedSeats array to generate "1. jegy", "2. jegy", etc.
 *
 * @param seatId - The ID of the seat/ticket
 * @param selectedSeats - Array of selected seat IDs
 * @returns string label like "1. jegy"
 */
export function getGeneralSeatLabel(seatId: string, selectedSeats: string[]): string {
    const index = selectedSeats.indexOf(seatId)

    if (index === -1) {
        // Seat not in selectedSeats array
        return 'Ismeretlen jegy'
    }

    // +1 because we want 1-based numbering
    return `${index + 1}. jegy`
}
